/*
  PLUSH + OAK — FREIGHT NOTE
  --------------------------------------------------------------------------
  The configurator shipped a running "Spend $X more to qualify for free
  shipping." line above the delivery estimator. On a $2,678 bed it read
  "Spend $7,321 more" — telling the shopper they had fallen short of a
  threshold that, across a full year of orders, only four web checkouts ever
  reached. That line is suppressed.

  In its place, a panel that stays hidden until the estimator actually returns
  a quote, then expands beneath it to present the rate as a feature. Nothing
  claims a price before the shopper has given a destination, which is what
  made the estimator feel pointless, and is also the only way this can be
  correct: the contiguous-US flat rate, the Alaska/Hawaii weight bands and the
  Canadian postal quotes are all different numbers.

  Every figure is read back out of the estimator's own result line rather than
  hardcoded, so there is one source of truth and no way for this to drift from
  what the delivery profile charges. The four inclusion claims are only shown
  against the Unlimited service, which is the flat rate; every other service
  falls back to the two claims that hold everywhere.

  The reveal is deliberately restrained. This is a $2,700 considered purchase,
  so the panel expands, the icon settles and the four ticks land in sequence —
  enough to read as a reward for answering, without the confetti register that
  would cheapen the product. All of it is skipped under prefers-reduced-motion.

  Loaded from theme.liquid on bed product pages only (product.type gate).
*/
(function () {
  'use strict';

  /* Every line closes off a way the price could still move. Verified against a
     year of US orders: tax is $0 and duties are null on all of them, so the
     total a shopper sees really is subtotal plus this rate. */
  var FLAT_POINTS = ['Complimentary shipping insurance',
                     'Duties, taxes and fees included',
                     'No tailgate or residential surcharge',
                     'One flat fee, per cart'];
  /* Canada runs its own Unlimited service — verified flat at the same figure in
     Ontario, BC, Alberta and Nova Scotia. It must NOT inherit the list above:
     GST/PST/HST is added at Canadian checkout, so "duties, taxes and fees
     included" would be a straight falsehood there. */
  var CANADA_POINTS = ['Complimentary shipping insurance',
                       'Same flat rate to every province',
                       'One flat fee, per cart'];

  /* Alaska and Hawaii ride weight bands rather than a flat rate, so only the
     two claims that hold everywhere are made. */
  var SAFE_POINTS = ['Quoted before you buy', 'Fully insured door to door'];

  /* The rate name is the only reliable signal for which list applies — the
     estimator returns "Unlimited Furniture Delivery (USA)" or "(Canada)", and
     anything else is a banded freight service. */
  function serviceKind(service) {
    if (!/unlimited/i.test(service)) return 'other';
    return /canada/i.test(service) ? 'ca' : 'us';
  }

  var SPEED_LABEL = 'Manufacturing Speed';

  /* Warranty leads: it answers the risk question at the moment of commitment,
     where provenance is only reassurance.

     The terms differ by model — the electric beds carry a shorter term on
     their motion parts than on the frame — so the headline is looked up by
     product.type, which theme.liquid passes in on the script tag. These two
     strings are the only place the numbers live; correct them here and both
     models follow. */
  var QUALITY_NOTE = 'Artisan crafted to order in Canada + the USA';
  var QUALITY_LEAD_DEFAULT = '3-year warranty on frame, stitching and suspension';
  var QUALITY_LEAD_BY_TYPE = {
    'Electric Salon Bed': '3-year warranty on frame and seams, 2-year on motion parts'
  };

  function qualityLead() {
    var tag = document.querySelector('script[data-po-product-type]');
    var type = tag ? (tag.getAttribute('data-po-product-type') || '') : '';
    return QUALITY_LEAD_BY_TYPE[type] || QUALITY_LEAD_DEFAULT;
  }

  var TRUST_FIND    = 'freight calculated at checkout';
  var TRUST_REPLACE = 'Ships fully insured, door to door';

  var BLOCK_CLASS = 'po-freight';
  var STYLE_ID    = 'po-freight-note-style';

  var ICON_BOX =
    '<svg class="po-freight__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
    'stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
    '<path d="M21 8.2 12 3 3 8.2v7.6L12 21l9-5.2V8.2Z"/><path d="m3 8.2 9 5.2 9-5.2"/>' +
    '<path d="M12 13.4V21"/></svg>';

  var ICON_SHIELD =
    '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.4" ' +
    'stroke-linecap="round" stroke-linejoin="round">' +
    '<path d="M8 1.4 2.6 3.6v4.1c0 3 2.2 5.4 5.4 6.9 3.2-1.5 5.4-3.9 5.4-6.9V3.6L8 1.4Z"/>' +
    '<path d="M5.7 7.9 7.3 9.5l3-3.2"/></svg>';

  var ICON_TICK =
    '<svg class="po-freight__tick" viewBox="0 0 16 16" fill="none" aria-hidden="true">' +
    '<circle cx="8" cy="8" r="7" fill="currentColor" opacity=".13"/>' +
    '<path d="M4.9 8.2 6.9 10.2 11.1 5.9" stroke="currentColor" stroke-width="1.7" ' +
    'stroke-linecap="round" stroke-linejoin="round"/></svg>';

  var CSS =
    /* Height is animated with grid-template-rows rather than a guessed
       max-height, so the expand lands exactly on the panel's real height. */
    '.po-freight-wrap{display:grid;grid-template-rows:0fr;' +
      'transition:grid-template-rows .36s cubic-bezier(.2,.7,.3,1);}' +
    '.po-freight-wrap.is-open{grid-template-rows:1fr;}' +
    '.po-freight-clip{overflow:hidden;min-height:0;}' +
    '.po-freight{margin:14px 0 0;padding:15px 16px;border-radius:10px;background:#fff;' +
      'border:1px solid rgba(0,0,0,.09);box-shadow:0 1px 2px rgba(0,0,0,.05);}' +
    '.po-freight.is-new{animation:po-fr-in .42s cubic-bezier(.2,.7,.3,1) both,' +
      'po-fr-ring .9s ease-out .1s both;}' +
    '.po-freight__eyebrow{display:block;font-size:.68em;letter-spacing:.11em;' +
      'text-transform:uppercase;opacity:.5;margin:0 0 7px;}' +
    '.po-freight__head{display:flex;align-items:flex-start;gap:11px;}' +
    '.po-freight__icon{flex:0 0 auto;width:21px;height:21px;margin-top:1px;opacity:.8;}' +
    '.po-freight.is-new .po-freight__icon{animation:po-fr-pop .5s cubic-bezier(.2,.8,.3,1.4) .08s both;}' +
    '.po-freight__copy{min-width:0;}' +
    '.po-freight__rate{display:block;font-weight:600;font-size:1.06em;line-height:1.25;}' +
    '.po-freight__svc{display:block;margin-top:3px;font-size:.85em;opacity:.58;}' +
    '.po-freight__list{list-style:none;margin:13px 0 0;padding:0;display:grid;gap:7px;}' +
    '.po-freight__list li{display:flex;align-items:center;gap:9px;font-size:.9em;line-height:1.3;}' +
    '.po-freight.is-new .po-freight__list li{animation:po-fr-tick .34s cubic-bezier(.2,.7,.3,1) both;}' +
    '.po-freight__tick{flex:0 0 auto;width:15px;height:15px;}' +
    '@keyframes po-fr-in{from{opacity:0;transform:translateY(-7px)}to{opacity:1;transform:none}}' +
    '@keyframes po-fr-pop{0%{opacity:0;transform:scale(.6)}' +
      '60%{opacity:1;transform:scale(1.12)}100%{transform:scale(1)}}' +
    '@keyframes po-fr-tick{from{opacity:0;transform:translateX(-7px)}to{opacity:1;transform:none}}' +
    '@keyframes po-fr-ring{from{box-shadow:0 0 0 0 rgba(20,20,20,.18),0 1px 2px rgba(0,0,0,.05)}' +
      'to{box-shadow:0 0 0 12px rgba(20,20,20,0),0 1px 2px rgba(0,0,0,.05)}}' +
    /* Motion is decoration here; the panel must read identically without it. */
    '@media (prefers-reduced-motion:reduce){.po-freight-wrap{transition:none;}' +
      '.po-freight,.po-freight.is-new,.po-freight.is-new .po-freight__icon,' +
      '.po-freight.is-new .po-freight__list li{animation:none!important;}}';

  function injectStyle() {
    if (document.getElementById(STYLE_ID)) return;
    var s = document.createElement('style');
    s.id = STYLE_ID;
    s.textContent = CSS;
    document.head.appendChild(s);
  }

  function val(selector) {
    var el = document.querySelector(selector);
    return el ? (el.value || '') : '';
  }

  /* The estimator's own result line is the single source of truth for price.
     Anything without a currency figure is a prompt or an error, not a quote. */
  function parseResult() {
    var el = document.querySelector('[data-poc-est-result]');
    if (!el) return null;
    var text = (el.textContent || '').replace(/\s+/g, ' ').trim();
    if (!text) return null;
    var money = text.match(/\$[\d,]+(?:\.\d{2})?/);
    if (!money) return null;
    return {
      text: text,
      amount: money[0],
      service: text.split('—')[0].replace(/[\s–—-]+$/, '').trim()
    };
  }

  function isFree(amount) {
    return /^\$0(?:\.00)?$/.test(amount);
  }

  /* The old progress line only ever told the shopper what they were short of,
     against a threshold a year of orders says they will not reach. */
  function hideDeficit() {
    var quiet = document.querySelector('[data-poc-freeship-offer]');
    if (quiet && quiet.style.display !== 'none') quiet.style.display = 'none';
  }

  /* Once the panel carries the rate and the service name, the estimator's own
     result line is a duplicate of it. Hidden while the panel stands — but
     restored the moment there is no quote, because that same line is what
     carries the "choose your state" prompt and the no-rate error. */
  function setRawResultVisible(visible) {
    var el = document.querySelector('[data-poc-est-result]');
    if (!el) return;
    var want = visible ? '' : 'none';
    if (el.style.display !== want) el.style.display = want;
  }

  function removeBlock() {
    var wrap = document.querySelector('.po-freight-wrap');
    if (wrap && wrap.parentNode) wrap.parentNode.removeChild(wrap);
    animatedFor = null;
  }

  function renderBlock(result) {
    var estimate = document.querySelector('.poc-estimate');
    if (!estimate) return;

    var kind = serviceKind(result.service);
    var points = kind === 'us' ? FLAT_POINTS
               : kind === 'ca' ? CANADA_POINTS
               : SAFE_POINTS;

    /* Named the way the delivery profile names it, so the PDP and the checkout
       line agree. The sub-line carries the promise the bullets then prove; for
       any other service it carries the real service name instead, since that
       is not ours to rename. */
    var lead, sub;
    if (isFree(result.amount)) {
      lead = 'Delivery included';
      sub  = 'All-inclusive. No surprises at checkout.';
    } else if (kind === 'us') {
      lead = result.amount + ' Unlimited Delivery';
      sub  = 'All-inclusive. No surprises at checkout.';
    } else if (kind === 'ca') {
      lead = result.amount + ' Unlimited Delivery';
      sub  = 'Flat to every province. Taxes shown at checkout.';
    } else {
      lead = result.amount + ' Delivery';
      sub  = result.service;
    }

    /* Naming the destination back to the shopper is what makes this read as
       their rate rather than a generic claim. */
    var state = val('select[data-poc-est-province]');
    var eyebrow = state ? 'Your rate · ' + state : 'Your rate';

    var wrap = document.querySelector('.po-freight-wrap');
    var built = false;
    if (!wrap) {
      wrap = document.createElement('div');
      wrap.className = 'po-freight-wrap';
      wrap.innerHTML =
        '<div class="po-freight-clip"><div class="' + BLOCK_CLASS + '">' +
        '<span class="po-freight__eyebrow"></span>' +
        '<div class="po-freight__head">' + ICON_BOX +
        '<span class="po-freight__copy"><span class="po-freight__rate"></span>' +
        '<span class="po-freight__svc"></span></span></div>' +
        '<ul class="po-freight__list"></ul></div></div>';
      /* Beneath the postcode check, the way Crate & Barrel order theirs. */
      estimate.parentNode.insertBefore(wrap, estimate.nextSibling);
      built = true;
    }

    var card   = wrap.querySelector('.' + BLOCK_CLASS);
    var browEl = card.querySelector('.po-freight__eyebrow');
    var rateEl = card.querySelector('.po-freight__rate');
    var svcEl  = card.querySelector('.po-freight__svc');
    var listEl = card.querySelector('.po-freight__list');

    /* Compare against what is rendered, never a marker attribute — the
       configurator rewrites this region and a flag would go stale. */
    if (browEl.textContent !== eyebrow) browEl.textContent = eyebrow;
    if (rateEl.textContent !== lead) rateEl.textContent = lead;
    if (svcEl.textContent !== sub) svcEl.textContent = sub;

    var current = [];
    for (var i = 0; i < listEl.children.length; i++) {
      current.push(listEl.children[i].textContent);
    }
    if (current.join('|') !== points.join('|')) {
      listEl.textContent = '';
      for (var j = 0; j < points.length; j++) {
        var li = document.createElement('li');
        li.innerHTML = ICON_TICK;
        var span = document.createElement('span');
        span.textContent = points[j];
        li.appendChild(span);
        /* Ticks land one after another rather than all at once. */
        li.style.animationDelay = (0.14 + j * 0.06).toFixed(2) + 's';
        listEl.appendChild(li);
      }
    }

    /* Replay the reveal only for a genuinely new quote. The 400ms poll calls
       through here constantly and must not retrigger anything. */
    var stamp = result.text + '|' + eyebrow;
    if (animatedFor !== stamp) {
      animatedFor = stamp;
      card.classList.remove('is-new');
      void card.offsetWidth;
      card.classList.add('is-new');
    }

    if (built) {
      requestAnimationFrame(function () { wrap.classList.add('is-open'); });
    } else if (!wrap.classList.contains('is-open')) {
      wrap.classList.add('is-open');
    }
  }

  /* The trust list promised "freight calculated at checkout", which describes
     exactly what our competitors do. Left price-free, since nothing should
     name a rate before a destination has been given. */
  function applyTrust() {
    var items = document.querySelectorAll('.poc-trust li span');
    for (var i = 0; i < items.length; i++) {
      var text = (items[i].textContent || '').replace(/\s+/g, ' ');
      if (text === TRUST_REPLACE) continue;
      if (text.indexOf(TRUST_FIND) === -1) continue;
      items[i].textContent = TRUST_REPLACE;
    }
  }

  /* The build window is already stated twice further up — once in the summary
     rows and once in the line under the price — so this block was a third
     telling of the same two facts, in the best space on the page.

     Rather than restyle something else to match it, the block itself is cloned:
     same element, same classes, so it is pixel-identical by construction. The
     clone has its data hooks stripped, which is what stops the configurator
     rewriting the copy back to shipping dates on every change. The original is
     hidden, and the trust list it replaces goes with it. */
  function buildQualityNote() {
    var shipnote = document.querySelector('[data-poc-shipnote]');
    if (!shipnote) return;

    var existing = document.querySelector('.po-quality');
    if (!existing) {
      var clone = shipnote.cloneNode(true);
      clone.removeAttribute('data-poc-shipnote');
      clone.removeAttribute('style');
      clone.className = 'poc-shipnote po-quality';

      var icon = clone.querySelector('.poc-shipnote__icon');
      if (icon) icon.innerHTML = ICON_SHIELD;

      var lead = clone.querySelector('[data-poc-shipwindow]');
      if (lead) {
        lead.removeAttribute('data-poc-shipwindow');
        lead.textContent = qualityLead();
      }

      var note = clone.querySelector('.poc-shipnote__note');
      if (note) {
        note.removeAttribute('data-poc-shiptransit');
        note.textContent = QUALITY_NOTE;
      }

      shipnote.parentNode.insertBefore(clone, shipnote);
      existing = clone;
    }

    if (shipnote.style.display !== 'none') shipnote.style.display = 'none';

    /* Its two claims now live in the block above, so the list is redundant. */
    var trust = document.querySelector('.poc-trust');
    if (trust && trust.style.display !== 'none') trust.style.display = 'none';
  }

  /* "Ships in — 6–8 weeks" states a duration without saying it is a choice, so
     the Rush upgrade below reads as unrelated. Naming the row for the decision
     and the value for the option taken makes the two obviously the same lever.

     The weeks themselves are left exactly as the configurator computed them —
     it already swaps 6–8 for 3–4 when Rush is ticked, and electric beds run
     longer — so this only ever wraps the number it finds. */
  function relabelSpeed() {
    var rows = document.querySelectorAll('[data-poc-spec-rows] .poc-summary__row');
    var rush = document.querySelector('[data-poc-rush]');
    var isRush = !!(rush && rush.checked);

    for (var i = 0; i < rows.length; i++) {
      var dt = rows[i].querySelector('dt');
      var dd = rows[i].querySelector('dd');
      if (!dt || !dd) continue;

      var label = (dt.textContent || '').replace(/\s+/g, ' ').trim();
      if (label !== 'Ships in' && label !== SPEED_LABEL) continue;

      /* The configurator rewrites this region wholesale, so the value may be
         raw again or may still carry a wrapper from the last pass. */
      var raw = (dd.textContent || '').replace(/\s+/g, ' ').trim();
      var wrapped = raw.match(/\(([^)]*)\)\s*$/);
      var weeks = wrapped ? wrapped[1] : raw;
      var want = (isRush ? 'Rush (' : 'Standard (') + weeks + ')';

      if (dt.textContent !== SPEED_LABEL) dt.textContent = SPEED_LABEL;
      if (dd.textContent !== want) dd.textContent = want;
    }
  }

  var lastQuote = '';
  var quotedFor = null;
  var animatedFor = null;

  function apply() {
    injectStyle();
    hideDeficit();
    applyTrust();
    buildQualityNote();
    relabelSpeed();

    var result = parseResult();
    var key = val('select[data-poc-est-province]') + '|' +
              val('[data-poc-est-zip]').toUpperCase().replace(/\s+/g, '');

    /* A new quote claims the destination that produced it. */
    if (result && result.text !== lastQuote) {
      lastQuote = result.text;
      quotedFor = key;
    }

    /* Editing the state or postcode after a quote invalidates it — hide the
       panel rather than leave a rate standing against a new destination. */
    if (!result || quotedFor !== key) {
      removeBlock();
      setRawResultVisible(true);
      return;
    }

    renderBlock(result);
    setRawResultVisible(false);
    rememberDestination();
  }

  /* The cart has no estimator of its own, so the destination answered here is
     kept for it. Only the destination — never the price, which must be
     recomputed against whatever the cart actually holds by then. */
  function rememberDestination() {
    var country = document.querySelector('[data-poc-est-country]');
    var payload = {
      country: country ? (country.value || '') : '',
      province: val('select[data-poc-est-province]'),
      zip: val('[data-poc-est-zip]')
    };
    if (!payload.country || !payload.province || !payload.zip) return;
    try {
      localStorage.setItem('po-destination', JSON.stringify(payload));
    } catch (e) {}
  }

  var queued = false;
  function schedule() {
    if (queued) return;
    queued = true;
    requestAnimationFrame(function () { queued = false; apply(); });
  }

  function start() {
    apply();
    var scope = document.querySelector('.poc-summary') || document.body;
    try {
      new MutationObserver(schedule).observe(scope, { childList: true, subtree: true, characterData: true });
    } catch (e) {}
    /* Choosing a state mutates no attribute, so the observer never sees it,
       and the events alone left the panel a step behind whatever fired first.
       Both are handled for immediacy, then a slow poll guarantees the panel is
       correct however the value got set. apply() exits early when nothing has
       changed, so the poll costs a few selector lookups. */
    document.addEventListener('change', schedule, true);
    document.addEventListener('input', schedule, true);
    setInterval(apply, 400);
    var delays = [300, 800, 1600, 2600];
    for (var i = 0; i < delays.length; i++) setTimeout(apply, delays[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();